import React from 'react';
import { useNavigate } from 'react-router';
import { customListRecordApi } from '../../apis';
import {
  InternalApiCustomList,
  InternalApiCustomListRecord,
  InternalApiCustomListSchemaField,
  InternalApiCustomListSchemaFieldType,
} from '../../api-internal';
import { Col, Row } from 'react-bootstrap';
import { createColumnsForObjectData, SalvButton, SalvTable, Size } from '@salvapp/ui-components';
import Has from '../../auth/Has';
import { CAN_MODIFY_CUSTOM_LIST_RECORD } from '../../auth/permissions';
import { findNonConformingAttributes, initInitialValues } from './helper';
import { RecordGroup } from './CustomListRecordPage';
import { showConfirmationModal } from '../../confirmation-modal/confirmationModalReducer';
import { useDispatch } from 'react-redux';
import { faPenToSquare } from '@fortawesome/pro-solid-svg-icons/faPenToSquare';
import { faBoxArchive } from '@fortawesome/pro-solid-svg-icons/faBoxArchive';
import { CustomListRecordHeaderSubTitle } from './CustomListRecordHeaderSubTitle';
import { ContentLinkify } from '../../common/ContentLinkify';
import SalvPage from '../../common/SalvPage';
import UserInfo from './UserInfo';
import { NonConformingBanner, NonConformingRawData } from './NonConformingRecordData';

interface Props {
  customList: InternalApiCustomList | undefined;
  schemaFields: InternalApiCustomListSchemaField[];
  record: InternalApiCustomListRecord | undefined;
  toggle: () => void;
  groupedFields: RecordGroup[];
}

const ViewCustomListRecord = (props: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { customList, schemaFields, record, toggle, groupedFields } = props;
  const canEdit = customList?.canEdit ?? false;
  const isActive = !record?.endTime;

  const archiveRecord = () => {
    customListRecordApi
      .deleteRecord({
        customListId: customList!.id!,
        id: record!.id!,
      })
      .then(() => navigate('/custom-lists/' + customList?.id));
  };

  const onRecordArchive = () => {
    dispatch(
      showConfirmationModal({
        title: 'Are you sure?',
        description: 'This will archive current custom list entry.',
        confirmationText: 'Yes, archive',
        cancelText: 'Cancel',
        onConfirm: async () => {
          archiveRecord();
        },
      })
    );
  };

  const values: { [key: string]: any } = initInitialValues(schemaFields, record);
  const { nonConformingEntries, nonConformingFieldNames } = findNonConformingAttributes(schemaFields, record);

  const renderTextFields = (items: InternalApiCustomListSchemaField[]) => {
    return (
      <ContentLinkify>
        <Row key={items.map((i) => i.id).join(',')}>
          {items.map((field, index) => {
            const value = values[field.fieldName];
            return (
              <Col key={index} md={4} className='mb-4'>
                <div className='row'>
                  <div className='col'>
                    <div className='field-title m-b-8 text-m'>{field.displayName}</div>
                    <div className='text-m'>{Array.isArray(value) ? value.join(', ') : (value ?? '')}</div>
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>
      </ContentLinkify>
    );
  };

  const renderObject = (field: InternalApiCustomListSchemaField) => {
    const childSchemaFields = schemaFields.filter((schemaField) => schemaField.parentId === field.id);
    const columns = createColumnsForObjectData<Record<string, string>>(
      childSchemaFields.map((sf) => ({
        accessorKey: sf.fieldName,
        header: sf.displayName,
        cell: (row: Record<string, string>) => <ContentLinkify>{row[sf.fieldName]}</ContentLinkify>,
      }))
    );
    const fieldValue = values[field.fieldName];
    const data = [
      childSchemaFields.reduce(
        (row, sf) => {
          row[sf.fieldName] = (fieldValue as Record<string, string>)?.[sf.fieldName] ?? '';
          return row;
        },
        {} as Record<string, string>
      ),
    ];
    return (
      <div key={field.id}>
        <div className='field-title text-m m-b-8'>{field.displayName}</div>
        <SalvTable columns={columns} data={data} />
      </div>
    );
  };

  const renderMulti = (field: InternalApiCustomListSchemaField) => {
    const columns = createColumnsForObjectData<{ value: string }>([
      {
        accessorKey: 'value',
        header: 'Value',
        cell: (row: { value: string }) => <ContentLinkify>{row.value}</ContentLinkify>,
      },
    ]);
    const data = (values[field.fieldName] ?? []).map((v: string) => ({ value: v }));
    return (
      <div key={field.id}>
        <div className='field-title text-m m-b-8'>{field.displayName}</div>
        <SalvTable columns={columns} data={data} />
      </div>
    );
  };

  const renderMultiObject = (field: InternalApiCustomListSchemaField) => {
    const childSchemaFields = schemaFields.filter((schemaField) => schemaField.parentId === field.id);
    const columns = createColumnsForObjectData<Record<string, string>>(
      childSchemaFields.map((sf) => ({
        accessorKey: sf.fieldName,
        header: sf.displayName,
        cell: (row: Record<string, string>) => <ContentLinkify>{row[sf.fieldName]}</ContentLinkify>,
      }))
    );
    const data = (values[field.fieldName] ?? []).map((row: Record<string, string>) =>
      childSchemaFields.reduce(
        (obj, sf) => {
          obj[sf.fieldName] = row?.[sf.fieldName] ?? '';
          return obj;
        },
        {} as Record<string, string>
      )
    );
    return (
      <div key={field.id}>
        <div className='field-title text-m m-b-8'>{field.displayName}</div>
        <SalvTable columns={columns} data={data} />
      </div>
    );
  };

  const renderGroup = (group: RecordGroup) => {
    const { isObject, isMulti, type } = group.key;
    if ((!isObject && !isMulti) || type === InternalApiCustomListSchemaFieldType.ENUM) {
      const conformingItems = group.items.filter((item) => !nonConformingFieldNames.has(item.fieldName));
      return conformingItems.length > 0 ? renderTextFields(conformingItems) : null;
    }
    if (isObject && !isMulti) {
      return group.items.filter((item) => !nonConformingFieldNames.has(item.fieldName)).map(renderObject);
    }
    if (!isObject && isMulti) {
      return group.items.filter((item) => !nonConformingFieldNames.has(item.fieldName)).map(renderMulti);
    }
    if (isObject && isMulti) {
      return group.items.filter((item) => !nonConformingFieldNames.has(item.fieldName)).map(renderMultiObject);
    }
  };

  return (
    <SalvPage
      title={isActive ? `Record ${record?.id || ''}` : `Archived record ${record?.id || ''}`}
      subTitle={<CustomListRecordHeaderSubTitle customList={customList} record={record} />}
      actions={
        isActive &&
        canEdit && (
          <Has allPermissions={[CAN_MODIFY_CUSTOM_LIST_RECORD]}>
            <SalvButton icon={faPenToSquare} label='Edit' size={Size.SMALL} onClick={() => toggle()} />
            <SalvButton
              icon={faBoxArchive}
              variant='danger-outline'
              label='Archive'
              size={Size.SMALL}
              onClick={() => onRecordArchive()}
            />
          </Has>
        )
      }
      style={{
        overflowWrap: 'anywhere',
      }}
    >
      <NonConformingBanner entries={nonConformingEntries} />
      {groupedFields.map((group, index) => (
        <div key={index} className='m-b-16'>
          {renderGroup(group)}
        </div>
      ))}
      <NonConformingRawData entries={nonConformingEntries} />
      {record && (
        <>
          <hr />
          <UserInfo
            description='Last updated by'
            userName={record.lastUpdatedBy?.name}
            time={record.lastUpdatedTime!}
            className={'m-t-24'}
          />
          <UserInfo
            description='Created by'
            userName={record.recordCreatedBy?.name}
            time={record.recordCreatedTime!}
            className={'m-t-12'}
          />
        </>
      )}
    </SalvPage>
  );
};

export default ViewCustomListRecord;
